import React, {useEffect, useMemo, useState} from 'react';
import {useHistory} from "react-router-dom";
import {CInput, CSelect} from '@coreui/react';
import Select from "react-select";
import {Bar} from "react-chartjs-2";
import {BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip} from "chart.js";
import {getApi} from "../../apis/backendApis";
import {OpcaoFiltro, SituacaoProcessoList, dataBr, textoDesvio} from "./modelo";
import '../../scss/styles-processo.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Paleta categorica de referencia (slots 1-3, validados todos-os-pares na
// superficie branca; o slot 3 fica abaixo de 3:1, por isso a tabela ao lado).
const SERIE = {abertos: '#2a78d6', finalizados: '#eb6834', encerrados: '#1baf7a'};
const numero = (valor: number | null | undefined) => Number(valor ?? 0).toLocaleString('pt-BR', {maximumFractionDigits: 1});
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

interface Dashboard {
    inicio: string,
    fim: string,
    indicadores: {[chave: string]: number | null},
    por_situacao: Array<{situacao: number, total: number}>,
    por_cliente: Array<{
        processo_id: number, cliente: string, fluxo: string, gestor: string, situacao: number, percentual: number,
        etapa_atual: string | null, inicio: string, previsao: string, desvio: number, itens: number, resolvidos: number,
        atrasados: number, pendencias_cliente: number, pendencias_equipe: number, ultimo_movimento: string | null,
    }>,
    por_mes: Array<{mes: string, abertos: number, finalizados: number, encerrados_sem_concluir: number}>,
    por_etapa: Array<{etapa: string, ordem: number, processos: number, processos_nela_agora: number, percentual_medio: number, desvio_medio: number, vencidas: number}>,
    por_responsavel: Array<{responsavel: string, abertos: number, atrasados: number, resolvidos_no_periodo: number}>,
    // So o que a tela mostra no periodo; cada lista respeita os outros filtros.
    filtros: {fluxos: OpcaoFiltro[], gestores: OpcaoFiltro[], clientes: OpcaoFiltro[]},
}

type Ordem = 'atraso' | 'cliente' | 'percentual' | 'previsao' | 'desvio';

const iso = (d: Date) => {
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
};

const PRESETS: Array<[string, () => [string, string]]> = [
    ['Este mês', () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth(), 1)), iso(new Date(h.getFullYear(), h.getMonth() + 1, 0))]; }],
    ['Últimos 3 meses', () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth() - 2, 1)), iso(new Date(h.getFullYear(), h.getMonth() + 1, 0))]; }],
    ['Últimos 6 meses', () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth() - 5, 1)), iso(new Date(h.getFullYear(), h.getMonth() + 1, 0))]; }],
    ['Este ano', () => { const h = new Date(); return [`${h.getFullYear()}-01-01`, `${h.getFullYear()}-12-31`]; }],
];

const Tile = ({rotulo, valor, detalhe, alerta}: {rotulo: string, valor: React.ReactNode, detalhe?: string, alerta?: boolean}) => (
    <div className={`pr-tile ${alerta ? 'alerta' : ''}`}>
        <span className="pr-tile-rotulo">{rotulo}</span>
        <strong className="pr-tile-valor">{valor}</strong>
        {detalhe && <span className="pr-tile-detalhe">{detalhe}</span>}
    </div>
);

/**
 * Dashboard gerencial dos processos (#480058, passo 9): fotografia dos
 * processos em aberto (por cliente, por etapa, por responsavel) e o que
 * aconteceu no periodo (abertos, finalizados, encerrados sem concluir).
 */
const DashboardProcessos = () => {
    const history = useHistory();
    const [periodo, setPeriodo] = useState<[string, string]>(PRESETS[2][1]());
    const [fluxo, setFluxo] = useState('');
    const [gestor, setGestor] = useState('');
    const [cliente, setCliente] = useState<{value: number, label: string} | null>(null);
    const [dados, setDados] = useState<Dashboard | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [ordem, setOrdem] = useState<Ordem>('atraso');
    const [tabelaMes, setTabelaMes] = useState(false);

    useEffect(() => {
        getApi().get('processo/dashboard', {params: {inicio: periodo[0], fim: periodo[1], fluxo_id: fluxo || undefined, gestor_id: gestor || undefined, cliente_id: cliente?.value}})
            .then(r => { setDados(r.data); setErro(null); })
            .catch(e => setErro(String(e?.response?.data?.error ?? 'Não foi possível carregar o dashboard.')));
    }, [periodo, fluxo, gestor, cliente]);

    const clientes = useMemo(() => {
        const lista = [...(dados?.por_cliente || [])];
        const comparadores: {[o in Ordem]: (a: any, b: any) => number} = {
            atraso: (a, b) => b.atrasados - a.atrasados || b.desvio - a.desvio,
            cliente: (a, b) => a.cliente.localeCompare(b.cliente),
            percentual: (a, b) => a.percentual - b.percentual,
            previsao: (a, b) => a.previsao.localeCompare(b.previsao),
            desvio: (a, b) => b.desvio - a.desvio,
        };
        return lista.sort(comparadores[ordem]);
    }, [dados, ordem]);

    const grafico = useMemo(() => {
        const meses = (dados?.por_mes || []).map(m => {
            const [ano, mes] = m.mes.split('-');
            return `${MESES[Number(mes) - 1]}/${ano.slice(2)}`;
        });
        const serie = (rotulo: string, cor: string, valores: number[]) => ({
            label: rotulo, data: valores, backgroundColor: cor, borderRadius: 4, borderSkipped: 'bottom' as const,
            maxBarThickness: 28, categoryPercentage: 0.7, barPercentage: 0.9,
        });
        return {
            labels: meses,
            datasets: [
                serie('Abertos', SERIE.abertos, (dados?.por_mes || []).map(m => m.abertos)),
                serie('Finalizados', SERIE.finalizados, (dados?.por_mes || []).map(m => m.finalizados)),
                serie('Cancelados ou arquivados', SERIE.encerrados, (dados?.por_mes || []).map(m => m.encerrados_sem_concluir)),
            ],
        };
    }, [dados]);

    // Opcoes dos filtros vem com o dashboard: so o que tem algo no periodo.
    const opcoes = dados?.filtros;
    const opcoesClientes = useMemo(() => (opcoes?.clientes || []).map(c => ({value: c.id, label: c.nome})), [opcoes]);
    const k = dados?.indicadores || {};
    const maiorSituacao = Math.max(1, ...(dados?.por_situacao || []).map(s => s.total));
    const maiorResponsavel = Math.max(1, ...(dados?.por_responsavel || []).map(r => r.abertos));
    const titulo = (rotulo: string, chave: Ordem) => (
        <button type="button" className={`pr-ordenar ${ordem === chave ? 'ativo' : ''}`} onClick={() => setOrdem(chave)}>{rotulo}</button>
    );

    return (
        <div className="pr-dash">
            <header className="pr-geral-topo">
                <div>
                    <h4>Dashboard de processos</h4>
                    <span>Processos em aberto hoje e o que aconteceu em {dataBr(periodo[0])} – {dataBr(periodo[1])}</span>
                </div>
                <div className="pr-geral-filtros">
                    <div className="pr-presets">
                        {PRESETS.map(([rotulo, gera]) => {
                            const [de, ate] = gera();
                            return (
                                <button type="button" key={rotulo} className={periodo[0] === de && periodo[1] === ate ? 'ativo' : ''} onClick={() => setPeriodo([de, ate])}>{rotulo}</button>
                            );
                        })}
                    </div>
                    <CInput type="date" value={periodo[0]} onChange={(e: any) => e.target.value && setPeriodo([e.target.value, periodo[1]])} title="Início do período"/>
                    <CInput type="date" value={periodo[1]} onChange={(e: any) => e.target.value && setPeriodo([periodo[0], e.target.value])} title="Fim do período"/>
                    <CSelect value={fluxo} onChange={(e: any) => setFluxo(e.target.value)}>
                        <option value="">Todos os fluxos</option>
                        {opcoes?.fluxos.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                    </CSelect>
                    <CSelect value={gestor} onChange={(e: any) => setGestor(e.target.value)}>
                        <option value="">Todos os gestores</option>
                        {opcoes?.gestores.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                    </CSelect>
                    <div className="pr-geral-cliente">
                        <Select options={opcoesClientes} value={cliente} isClearable placeholder="Todos os clientes"
                                noOptionsMessage={() => 'Nenhum cliente com processos no período'}
                                onChange={(valor: any) => setCliente(valor)} classNamePrefix="pr-select"/>
                    </div>
                </div>
            </header>
            {erro && <div className="pr-aviso">{erro}</div>}

            {dados && (
                <div className="pr-dash-corpo">
                    <section className="pr-tiles">
                        <Tile rotulo="Em aberto" valor={k.em_aberto ?? 0}
                              detalhe={`${k.executando} executando · ${k.aguardando_inicio} aguardando início · ${k.congelados} congelados`}/>
                        <Tile rotulo="Com itens atrasados" valor={k.com_atraso ?? 0} alerta={(k.com_atraso ?? 0) > 0} detalhe="processos em aberto"/>
                        <Tile rotulo="% médio concluído" valor={`${numero(k.percentual_medio)}%`} detalhe="dos processos em aberto"/>
                        <Tile rotulo="Desvio médio" valor={textoDesvio(Math.round(Number(k.desvio_medio ?? 0)))} alerta={Number(k.desvio_medio) > 0}
                              detalhe="previsão atual × meta original"/>
                        <Tile rotulo="Pendências" valor={`${k.pendencias_cliente} · ${k.pendencias_equipe}`} detalhe="do cliente · da equipe"/>
                        <Tile rotulo="Abertos no período" valor={k.abertos_no_periodo ?? 0}/>
                        <Tile rotulo="Finalizados no período" valor={k.finalizados_no_periodo ?? 0}
                              detalhe={k.duracao_media_finalizados ? `em média ${numero(k.duracao_media_finalizados)} dias úteis` : undefined}/>
                        <Tile rotulo="Encerrados sem concluir" valor={(k.cancelados_no_periodo ?? 0) + (k.arquivados_no_periodo ?? 0)}
                              detalhe={`${k.cancelados_no_periodo} cancelados · ${k.arquivados_no_periodo} arquivados`}/>
                    </section>

                    <section className="pr-dash-linha">
                        <div className="pr-painel-dash largo">
                            <div className="pr-painel-dash-topo">
                                <h5>Processos em aberto por cliente <em>{clientes.length}</em></h5>
                                <span className="pr-ajuda">Clique para abrir o processo</span>
                            </div>
                            <div className="pr-tabela-rolagem">
                                <table className="pr-tabela">
                                    <thead>
                                        <tr>
                                            <th>{titulo('Cliente', 'cliente')}</th>
                                            <th>Etapa atual</th>
                                            <th className="num">{titulo('%', 'percentual')}</th>
                                            <th className="num">{titulo('Previsão', 'previsao')}</th>
                                            <th className="num">{titulo('Desvio', 'desvio')}</th>
                                            <th className="num">{titulo('Atrasados', 'atraso')}</th>
                                            <th className="num">Do cliente</th>
                                            <th>Gestor</th>
                                            <th>Último movimento</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientes.map(c => (
                                            <tr key={c.processo_id} onClick={() => history.push(`/processos/${c.processo_id}`)}>
                                                <td><strong>{c.cliente}</strong><span className="pr-sub">{c.fluxo} · {SituacaoProcessoList.getDescription(c.situacao)}</span></td>
                                                <td>{c.etapa_atual || '—'}</td>
                                                <td className="num">
                                                    <span className="pr-mini-barra"><span style={{width: `${c.percentual}%`}}/></span>
                                                    {Math.round(c.percentual)}%
                                                </td>
                                                <td className="num">{dataBr(c.previsao)}</td>
                                                <td className={`num ${c.desvio > 0 ? 'ruim' : ''}`}>{textoDesvio(c.desvio)}</td>
                                                <td className={`num ${c.atrasados > 0 ? 'ruim' : ''}`}>{c.atrasados}</td>
                                                <td className="num">{c.pendencias_cliente}</td>
                                                <td>{c.gestor}</td>
                                                <td>{c.ultimo_movimento ? dataBr(c.ultimo_movimento) : '—'}</td>
                                            </tr>
                                        ))}
                                        {clientes.length === 0 && <tr><td colSpan={9} className="pr-vazio">Nenhum processo em aberto com estes filtros.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="pr-painel-dash">
                            <div className="pr-painel-dash-topo"><h5>Situação de todos os processos</h5></div>
                            {dados.por_situacao.map(s => (
                                <div key={s.situacao} className="pr-barra-rotulada" title={`${SituacaoProcessoList.getDescription(s.situacao)}: ${s.total}`}>
                                    <span>{SituacaoProcessoList.getDescription(s.situacao)}</span>
                                    <span className="pr-trilho"><span style={{width: `${s.total * 100 / maiorSituacao}%`}}/></span>
                                    <strong>{s.total}</strong>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="pr-dash-linha">
                        <div className="pr-painel-dash largo">
                            <div className="pr-painel-dash-topo">
                                <h5>Resumo por período</h5>
                                <button type="button" className="pr-link" onClick={() => setTabelaMes(!tabelaMes)}>{tabelaMes ? 'Ver gráfico' : 'Ver tabela'}</button>
                            </div>
                            {!tabelaMes ? (
                                <div className="pr-grafico">
                                    <Bar data={grafico} options={{
                                        maintainAspectRatio: false,
                                        interaction: {mode: 'index', intersect: false},
                                        plugins: {
                                            legend: {position: 'top', align: 'start', labels: {boxWidth: 12, boxHeight: 12, usePointStyle: true, pointStyle: 'rectRounded', color: '#52514e'}},
                                            tooltip: {padding: 10, boxPadding: 4},
                                        },
                                        scales: {
                                            x: {grid: {display: false}, ticks: {color: '#898781'}, border: {color: '#c3c2b7'}} as any,
                                            y: {beginAtZero: true, ticks: {precision: 0, color: '#898781'}, grid: {color: '#e1e0d9'}, border: {display: false}} as any,
                                        },
                                    }}/>
                                </div>
                            ) : (
                                <table className="pr-tabela compacta">
                                    <thead><tr><th>Mês</th><th className="num">Abertos</th><th className="num">Finalizados</th><th className="num">Cancelados ou arquivados</th></tr></thead>
                                    <tbody>
                                        {dados.por_mes.map((m, i) => (
                                            <tr key={m.mes}><td>{grafico.labels[i]}</td><td className="num">{m.abertos}</td><td className="num">{m.finalizados}</td><td className="num">{m.encerrados_sem_concluir}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="pr-painel-dash">
                            <div className="pr-painel-dash-topo"><h5>Por responsável</h5><span className="pr-ajuda">itens em aberto</span></div>
                            <table className="pr-tabela compacta">
                                <thead><tr><th>Pessoa</th><th className="num">Abertos</th><th className="num">Atrasados</th><th className="num" title="Resolvidos no período">Resolvidos</th></tr></thead>
                                <tbody>
                                    {dados.por_responsavel.slice(0, 15).map(r => (
                                        <tr key={r.responsavel}>
                                            <td>{r.responsavel}</td>
                                            <td className="num"><span className="pr-mini-barra"><span style={{width: `${r.abertos * 100 / maiorResponsavel}%`}}/></span>{r.abertos}</td>
                                            <td className={`num ${r.atrasados ? 'ruim' : ''}`}>{r.atrasados}</td>
                                            <td className="num">{r.resolvidos_no_periodo}</td>
                                        </tr>
                                    ))}
                                    {dados.por_responsavel.length === 0 && <tr><td colSpan={4} className="pr-vazio">Ninguém com itens.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section className="pr-painel-dash">
                        <div className="pr-painel-dash-topo"><h5>Andamento por etapa</h5><span className="pr-ajuda">processos em aberto</span></div>
                        <table className="pr-tabela">
                            <thead>
                                <tr>
                                    <th>Etapa</th>
                                    <th className="num">Processos nela agora</th>
                                    <th>% médio concluído</th>
                                    <th className="num">Desvio médio</th>
                                    <th className="num">Com prazo vencido</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dados.por_etapa.map(e => (
                                    <tr key={e.etapa}>
                                        <td><strong>{e.ordem}. {e.etapa}</strong></td>
                                        <td className="num">{e.processos_nela_agora}</td>
                                        <td><span className="pr-barra-etapa"><span style={{width: `${e.percentual_medio}%`}}/></span> {Math.round(e.percentual_medio)}%</td>
                                        <td className={`num ${e.desvio_medio > 0 ? 'ruim' : ''}`}>{textoDesvio(Math.round(e.desvio_medio))}</td>
                                        <td className={`num ${e.vencidas ? 'ruim' : ''}`}>{e.vencidas}</td>
                                    </tr>
                                ))}
                                {dados.por_etapa.length === 0 && <tr><td colSpan={5} className="pr-vazio">Nenhum processo em aberto.</td></tr>}
                            </tbody>
                        </table>
                    </section>
                </div>
            )}
        </div>
    );
};

export default DashboardProcessos;
